// One place that books an appointment from a lead screen: calendar event, disposition, then the optional
// confirmation/reminder texts and confirmation email. The event itself is what matters; a failed
// notification is reported but never undoes the booking.
import { apiPost } from './api.js';

/**
 * @param {string} leadId
 * @param {object} p - Event from appointmentForm.buildEvent (or the legacy {startAt,endAt,title,notes,syncedTo}).
 * @returns {Promise<{ok: boolean, warnings: string[]}>} ok=false only when the event itself could not be created.
 */
export async function bookAppointment(leadId, p) {
  await apiPost('/calendar', {
    leadId, title: p.title, startAt: p.startAt, endAt: p.endAt, notes: p.notes, syncedTo: p.syncedTo,
    ...(p.timezone ? { timezone: p.timezone } : {}), ...(p.busy === false ? { busy: false } : {}),
  });
  const warnings = [];

  // Disposition: legacy callers (no applyDisposition flag) keep the original BOOKED_APPOINTMENT behaviour.
  try {
    if (p.applyDisposition === false) { /* the agent switched the action off */ }
    else if (p.dispositionId) await apiPost('/dispositions/apply', { leadId, dispositionId: p.dispositionId });
    else await apiPost('/dispositions', { leadId, status: 'BOOKED_APPOINTMENT' });
  } catch (e) {
    warnings.push(e?.response?.data?.error || 'Could not apply the disposition');
  }

  const n = p.notifications || {};
  const attempt = async (label, fn) => {
    try { await fn(); } catch (e) { warnings.push(`${label}: ${e?.response?.data?.error || 'failed'}`); }
  };
  if (n.confirmationText) await attempt('Confirmation text', () => apiPost('/sms/send', { leadId, body: n.confirmationText }));
  if (n.reminderText) {
    if (new Date(n.reminderText.scheduledFor).getTime() <= Date.now()) warnings.push('Reminder text skipped: the reminder time has already passed');
    else await attempt('Reminder text', () => apiPost('/sms/send', { leadId, body: n.reminderText.body, scheduledFor: n.reminderText.scheduledFor }));
  }
  if (n.confirmationEmail) await attempt('Confirmation email', () => apiPost(`/leads/${leadId}/email`, n.confirmationEmail));
  return { ok: true, warnings };
}
